import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { statsAPI } from '../api';
import { User } from '../types';
import { formatDuration } from '../utils/format';
import {
  colors,
  fonts,
  radius,
  shadow,
  numeric,
  sectionLabel,
} from '../theme';

const StatsContainer = styled.div`
  padding: 36px 20px 20px;
  max-width: 620px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-family: ${fonts.display};
  font-size: 30px;
  text-align: center;
  margin: 0 0 24px;
`;

/* One bordered block split into cells, rather than a grid of floating cards —
   it reads as a single table of figures. */
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  background: ${colors.card};
  border: 1px solid ${colors.rule};
  border-radius: ${radius.md};
  box-shadow: ${shadow.panel};
  overflow: hidden;

  @media (max-width: 520px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Stat = styled.div`
  padding: 20px 12px;
  text-align: center;
  border-right: 1px solid ${colors.rule};
  border-bottom: 1px solid ${colors.rule};

  /* Trim the outer edges so the block keeps clean borders. */
  &:nth-child(3n) {
    border-right: none;
  }

  &:nth-last-child(-n + 3) {
    border-bottom: none;
  }

  @media (max-width: 520px) {
    &:nth-child(3n) {
      border-right: 1px solid ${colors.rule};
    }

    &:nth-child(2n) {
      border-right: none;
    }

    &:nth-last-child(-n + 3) {
      border-bottom: 1px solid ${colors.rule};
    }

    &:nth-last-child(-n + 2) {
      border-bottom: none;
    }
  }
`;

const StatValue = styled.div`
  ${numeric}
  font-size: 26px;
  font-weight: 700;
  color: ${colors.ink};
  line-height: 1.2;
`;

const StatLabel = styled.div`
  ${sectionLabel}
  margin-top: 4px;
`;

const Message = styled.div`
  text-align: center;
  padding: 56px 20px;
  font-family: ${fonts.display};
  font-size: 16px;
  color: ${colors.inkFaint};
`;

const EmptyNote = styled.p`
  margin-top: 18px;
  text-align: center;
  font-size: 14px;
  color: ${colors.inkFaint};
`;

interface UserStatsData {
  total_completed: number;
  average_time: number;
  best_time: number;
  total_time: number;
  daily_streak: number;
  puzzles_created: number;
}

interface UserStatsProps {
  currentUser: User | null;
}

const formatTotalTime = (seconds: number) => {
  if (!seconds) return '0m';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
};

export const UserStats: React.FC<UserStatsProps> = ({ currentUser }) => {
  const [stats, setStats] = useState<UserStatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!currentUser) {
      setError('Sign in to see your stats.');
      setLoading(false);
      return;
    }

    const loadStats = async () => {
      setLoading(true);
      try {
        setStats(await statsAPI.getUserStats());
        setError(null);
      } catch (err) {
        console.error('Error loading stats:', err);
        setError('Could not load your stats.');
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [currentUser]);

  if (loading) return <Message>Loading your stats…</Message>;
  if (error) return <Message>{error}</Message>;
  if (!stats) return <Message>No stats available.</Message>;

  return (
    <StatsContainer>
      <Title>Your stats</Title>

      <StatsGrid>
        <Stat>
          <StatValue>{stats.total_completed}</StatValue>
          <StatLabel>Solved</StatLabel>
        </Stat>
        <Stat>
          <StatValue>{formatDuration(stats.best_time)}</StatValue>
          <StatLabel>Best time</StatLabel>
        </Stat>
        <Stat>
          <StatValue>{formatDuration(stats.average_time)}</StatValue>
          <StatLabel>Average</StatLabel>
        </Stat>
        <Stat>
          <StatValue>{stats.daily_streak}</StatValue>
          <StatLabel>Day streak</StatLabel>
        </Stat>
        <Stat>
          <StatValue>{formatTotalTime(stats.total_time)}</StatValue>
          <StatLabel>Time played</StatLabel>
        </Stat>
        <Stat>
          <StatValue>{stats.puzzles_created}</StatValue>
          <StatLabel>Created</StatLabel>
        </Stat>
      </StatsGrid>

      {stats.total_completed === 0 && (
        <EmptyNote>Finish a puzzle and your numbers will show up here.</EmptyNote>
      )}
    </StatsContainer>
  );
};

export default UserStats;
